import type { MaterialsNodeData, PostBasesItem } from '../types';

export type PostBasesSource = Partial<
  Pick<
    MaterialsNodeData,
    | 'postBasesItems'
    | 'postBasesProductName'
    | 'postBasesQty'
    | 'postBasesUnitCost'
    | 'postBasesTotal'
  >
>;

export function postBasesItemTotal(item: Pick<PostBasesItem, 'quantity' | 'unitCost'>): number {
  return Math.round((Number(item.quantity) || 0) * (Number(item.unitCost) || 0) * 100) / 100;
}

function legacyPostBasesItem(materials: PostBasesSource): PostBasesItem | null {
  const quantity = materials.postBasesQty || 0;
  const hasContent =
    quantity > 0 || !!materials.postBasesProductName?.trim() || (materials.postBasesTotal || 0) > 0;
  if (!hasContent) return null;
  const unitCost = materials.postBasesUnitCost || 0;
  return {
    id: 'legacy-post-bases',
    productName: materials.postBasesProductName,
    quantity,
    unitCost,
    total: materials.postBasesTotal || postBasesItemTotal({ quantity, unitCost }),
  };
}

/** Explicit item list when present; otherwise one row from the legacy qty/unit fields. */
export function getStructurePostBasesItems(materials: PostBasesSource): PostBasesItem[] {
  const items = materials.postBasesItems;
  if (Array.isArray(items) && items.length > 0) {
    return items.map((item) => ({ ...item, total: postBasesItemTotal(item) }));
  }
  const legacy = legacyPostBasesItem(materials);
  return legacy ? [legacy] : [];
}

export function withPostBasesRollups<T extends PostBasesSource>(
  materials: T,
  rawItems: PostBasesItem[],
): T {
  const items = rawItems.map((item) => ({ ...item, total: postBasesItemTotal(item) }));
  const first = items[0];
  return {
    ...materials,
    postBasesItems: items,
    postBasesQty: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
    postBasesUnitCost: first?.unitCost ?? 80,
    postBasesTotal: items.reduce((sum, item) => sum + item.total, 0),
    postBasesProductName: first?.productName,
  };
}
